// CallModal — premium B2B replacement for the legacy CallUI.js.
//
// Drop-in compatible with the old component: same props (call, callType,
// onEndCall) and same mount point (must sit inside <StreamCall>).
//
// What's shipped here:
//   - Premium dark theme with TP color tokens (modal #1A1A2E, accent #4A90D9)
//   - Pulsing avatar + "Connecting…" state while CallingState ≠ JOINED
//   - Active audio: full-bleed avatar + animated Waveform visualizer
//   - Active video: SDK ParticipantView tiles inside our themed shell
//   - ActiveHeader: avatar + name + REC badge + live mm:ss timer + signal bars
//   - Encrypted chip footer
//   - End-call as styled pill ("End" + phone icon)
//   - Mic / Camera / Speaker device pickers in the control bar
//   - Background blur toggle (BackgroundFiltersProvider mounted in Inbox.js)
//   - Mini-window mode (compact 320x200 at bottom-left while call runs)
//   - Settings panel slide-in (Audio / Video / General tabs)
//   - Real Stream call recording via call.startRecording / stopRecording,
//     hidden when the call type's capabilities don't include record permission
//   - Add-participant overlay (search + invite via existing chat pattern)
//
// Why we keep the SDK's <SpeakerLayout> for video instead of building
// custom VideoTiles: re-implementing real video rendering would mean
// re-wiring <ParticipantView> ourselves with per-track subscriptions —
// out of scope for this redesign. We instead let the SDK handle the
// video tiles and override the surrounding chrome.

import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  CallingState,
  ParticipantView,
  SpeakerLayout,
  StreamTheme,
  useCallStateHooks,
  useParticipantViewContext,
  useBackgroundFilters,
} from "@stream-io/video-react-sdk";

import {
  TP,
  I,
  Icon,
  Avatar,
  Bars,
  SecureChip,
  Waveform,
  Timer,
  Chrome,
  CtrlButton,
  SourceMenu,
} from "./shared";
import useMediaDevices, { useMicLevel } from "../../../hooks/inbox/useMediaDevices";
import SettingsPanel from "./SettingsPanel";
import AddParticipantOverlay from "./AddParticipantOverlay";
import "./call.css";

// ─────────────────────────────────────────────────────────────────────
// Custom ParticipantViewUI — replaces the SDK's default overlay which
// was showing:
//   * raw numeric user IDs ("427", "383") because the upstream Stream
//     user records weren't named, so the SDK fell back to the ID;
//   * a 3-dot admin menu with options like "Pin for everyone",
//     "Block", "Allow audio", "Disable screen sharing", etc. — none
//     of which belong in a 1:1 call between two users;
//   * unrendered i18n keys like "{{ direction }} fullscreen" because
//     a translation variable was never resolved.
// We render only what's useful: a small name pill in the bottom-left.
// ─────────────────────────────────────────────────────────────────────
function TpParticipantUI() {
  const ctx = useParticipantViewContext();
  const participant = ctx?.participant;
  if (!participant) return null;

  const rawName = participant.name;
  const userId = participant.userId;
  // SDK falls back to the userId when no `name` is set. Treat anything
  // that's empty, equal to the userId, or purely numeric as a fallback
  // and show a friendlier placeholder instead.
  const isFallback =
    !rawName || rawName === userId || /^\d+$/.test(String(rawName));
  const isLocal = participant.isLocalParticipant;
  const displayName = isLocal
    ? "You"
    : isFallback
    ? "Participant"
    : rawName;

  return (
    <div
      style={{
        position: "absolute",
        bottom: 10,
        left: 10,
        background: "rgba(0,0,0,0.55)",
        color: "#fff",
        padding: "5px 10px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 500,
        pointerEvents: "none",
        maxWidth: "70%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        letterSpacing: 0.2,
      }}
    >
      {displayName}
    </div>
  );
}

export default function CallModal({ call, callType, onEndCall, chatClient }) {
  const {
    useCallCallingState,
    useMicrophoneState,
    useCameraState,
    useScreenShareState,
    useParticipantCount,
    useRemoteParticipants,
    useLocalParticipant,
    useIsCallRecordingInProgress,
    useOwnCapabilities,
  } = useCallStateHooks();

  const callingState = useCallCallingState();
  const { microphone, isMute } = useMicrophoneState();
  const { camera, isEnabled: isCameraEnabled } = useCameraState();
  const { status: screenShareStatus, screenShare } = useScreenShareState();
  const participantCount = useParticipantCount();
  const remoteParticipants = useRemoteParticipants();
  const localParticipant = useLocalParticipant();
  // Real recording state from the SDK observable. `false` if recording is
  // off OR if the call type isn't configured for recording at all.
  const isRecording = useIsCallRecordingInProgress();
  // Capabilities granted to the current user on this call. If the call
  // type doesn't enable recording (Stream Dashboard → Call Types →
  // recording_enabled) the capability is absent and we hide the button
  // up-front rather than showing a control that always errors.
  const ownCapabilities = useOwnCapabilities();
  const canRecord =
    Array.isArray(ownCapabilities) &&
    (ownCapabilities.includes("start-record-call") ||
      ownCapabilities.includes("stop-record-call"));

  // Local "in-flight" state so the user can't double-click the recording
  // button while the SDK is still negotiating with the backend.
  const [isRecordingPending, setIsRecordingPending] = useState(false);

  // Which device dropdown is open. Single value rather than three booleans
  // because at most one menu can be open at a time — and toggleDropdown()
  // re-uses the same state to close the previous one when switching.
  const [openMenu, setOpenMenu] = useState(null); // 'mic' | 'speaker' | 'cam' | null
  const toggleDropdown = (which) =>
    setOpenMenu((prev) => (prev === which ? null : which));
  const closeDropdown = () => setOpenMenu(null);

  // Mini-window mode — when on, the modal collapses to a 320x200 pinned
  // bottom-right floating window. We keep the SDK call alive; only the
  // outer container layout changes. Closing any open device dropdown when
  // minimizing avoids the menu floating off-screen.
  const [isMinimized, setIsMinimized] = useState(false);
  const toggleMinimize = () => {
    setIsMinimized((m) => !m);
    setOpenMenu(null);
    setShowSettings(false);
    setShowAddParticipant(false);
  };

  // Slide-in panels — Settings + Add-Participant. Mutually exclusive with
  // each other; both close when minimizing.
  const [showSettings, setShowSettings] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const openSettings = () => {
    setShowSettings(true);
    setShowAddParticipant(false);
    setOpenMenu(null);
  };
  const openAddParticipant = () => {
    setShowAddParticipant(true);
    setShowSettings(false);
    setOpenMenu(null);
  };

  // Speaker mute removed from the UI — the SDK has no real "mute output"
  // toggle and shipping a placebo control made users tap it expecting
  // silence and still hear the remote audio. Hidden until properly wired
  // to remote-track volume.

  // Device list + selection from Stream SDK (wrapped in our hook).
  const devices = useMediaDevices();
  // Audio level for the currently active mic — only meter while the mic
  // dropdown is actually open, so we don't keep an AudioContext alive
  // for the whole call.
  const micLevel = useMicLevel(openMenu === "mic");

  // Background blur — declared below `isVideo` to avoid TDZ; see further down.

  // We track our own joinedAt so the Timer keeps mm:ss while CallingState
  // is JOINED — the SDK exposes no consistent "session_start_ts" field.
  const [joinedAt, setJoinedAt] = useState(null);
  useEffect(() => {
    if (callingState === CallingState.JOINED && !joinedAt) {
      setJoinedAt(Date.now());
    }
  }, [callingState, joinedAt]);

  // Auto-enable microphone for audio calls — keep parity with old CallUI.
  useEffect(() => {
    if (callType === "audio" && microphone && isMute) {
      microphone.enable().catch((err) => {
        // Soft fail — the user can still toggle the mic manually. The
        // permission error path is handled in handleToggleMicrophone.
        console.error("Auto-enable microphone failed:", err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callType, microphone]);

  const isVideo = callType === "video";
  const isJoined = callingState === CallingState.JOINED;
  const isConnecting =
    callingState === CallingState.JOINING ||
    callingState === CallingState.RECONNECTING ||
    callingState === CallingState.MIGRATING;

  // Background blur (video calls only). The BackgroundFiltersProvider is
  // mounted by Inbox.js around <StreamCall>, so this hook always resolves
  // during an active call.
  // - `isSupported` is false on browsers without WebAssembly SIMD (older
  //   mobile Safari) — we hide the toggle in that case.
  // - `isReady` flips to true once the TFLite model (~3.4 MB) has finished
  //   streaming from /tf/. Until then the button shows a subtle loading
  //   state so the user sees that something is happening.
  const bgFilters = useBackgroundFilters();
  const blurActive = bgFilters.backgroundFilter === "blur";
  const blurSupported = bgFilters.isSupported && isVideo;
  const blurReady = bgFilters.isReady;
  const handleToggleBlur = () => {
    // If the browser doesn't support background filters at all, the button
    // is already hidden (see {blurSupported && ...} in the render tree),
    // so this branch is mostly defensive.
    if (!blurSupported) {
      toast.error("Background blur isn't supported in this browser.");
      return;
    }
    // The TFLite model + WASM (~3.4 MB) takes a couple of seconds to
    // download the first time. If the user taps the button before that
    // finishes, the previous build silently no-op'd — users (and Sachin's
    // QA) thought the button was broken. We now surface a toast and
    // visually disable the button via opacity + cursor in the render
    // below so it's clear something is happening.
    if (!blurReady) {
      toast("Background blur is still loading — try again in a second.", {
        icon: "⏳",
      });
      return;
    }
    if (blurActive) {
      bgFilters.disableBackgroundFilter();
    } else {
      // 'high' matches what most consumer apps default to. The SDK also
      // supports 'low' / 'medium' if we want a "subtle blur" preset later.
      bgFilters.applyBackgroundBlurFilter("high");
    }
  };

  // Derive the contact label from the first remote participant; fall back
  // to a generic placeholder while solo (e.g. you just dialed out).
  const remote = remoteParticipants?.[0];
  const contact = useMemo(
    () => ({
      name:
        remote?.name ||
        remote?.userId ||
        (isConnecting ? "Connecting…" : "Waiting for participant"),
      role: remote?.custom?.role || (isVideo ? "Video call" : "Audio call"),
    }),
    [remote, isVideo, isConnecting]
  );

  // ── Controls ──────────────────────────────────────────────────────────
  const handleToggleMic = async () => {
    if (!microphone) return;
    try {
      if (isMute) await microphone.enable();
      else microphone.disable();
    } catch (err) {
      console.error("Mic toggle failed:", err);
      if (err?.message?.includes("Permission was not granted")) {
        toast.error(
          "Microphone permission denied. Click the lock icon in your browser's address bar and allow microphone access, then refresh.",
          { duration: 8000 },
        );
      } else {
        toast.error("Could not toggle microphone.");
      }
    }
  };

  const handleToggleCamera = async () => {
    if (!camera) return;
    try {
      if (isCameraEnabled) camera.disable();
      else await camera.enable();
    } catch (err) {
      console.error("Camera toggle failed:", err);
      if (err?.message?.includes("Permission was not granted")) {
        toast.error(
          "Camera permission denied. Click the lock icon in your browser's address bar and allow camera access, then refresh.",
          { duration: 8000 },
        );
      } else {
        toast.error("Could not toggle camera.");
      }
    }
  };

  const handleToggleScreenShare = async () => {
    if (!screenShare) return;
    try {
      if (screenShareStatus === "enabled") await screenShare.disable();
      else await screenShare.enable();
    } catch (err) {
      console.error("Screen share toggle failed:", err);
      toast.error("Could not toggle screen sharing.");
    }
  };

  const handleToggleRecording = async () => {
    if (isRecordingPending || !call) return;
    setIsRecordingPending(true);
    try {
      if (isRecording) {
        await call.stopRecording();
      } else {
        await call.startRecording();
      }
    } catch (err) {
      console.error("Recording toggle failed:", err);
      // Permission errors land here when the call type isn't configured
      // for recording (Stream Dashboard → Call Types → recording_enabled).
      // We surface a user-friendly message instead of failing silently.
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Recording isn't available for this call.";
      toast.error(`Recording failed: ${message}`);
    } finally {
      setIsRecordingPending(false);
    }
  };

  // Device source change handlers — all SDK calls go through the
  // useMediaDevices hook which swallows + logs errors so we don't need
  // try/catch here. Closing the menu is handled by SourceMenu itself.
  const handleSelectMic = (deviceId) => devices.mic.select(deviceId);
  const handleSelectCamera = (deviceId) => devices.camera.select(deviceId);
  const handleSelectSpeaker = (deviceId) => devices.speaker.select(deviceId);

  const headerSubtitle = isJoined
    ? "End-to-end encrypted"
    : isConnecting
    ? "Connecting…"
    : "Preparing call";
  const headerAccent = isRecording
    ? TP.danger
    : isJoined
    ? TP.ok
    : TP.warn;

  // ─── Mini-Window Mode ──────────────────────────────────────────────
  // Renders a compact 320x200 floating widget at bottom-LEFT (the
  // support icon already lives at bottom-right). The SDK call stays
  // connected; only the chrome changes. Click the expand button to
  // come back to full view.
  if (isMinimized) {
    return (
      <MiniWindow
        contact={contact}
        callType={callType}
        muted={isMute}
        onMute={handleToggleMic}
        cameraOff={!isCameraEnabled}
        onCamera={handleToggleCamera}
        onExpand={toggleMinimize}
        onEnd={onEndCall}
        joinedAt={joinedAt}
        isJoined={isJoined}
      />
    );
  }

  return (
    <div
      className="tp-call-modal"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "rgba(8,10,20,0.85)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: "Inter, -apple-system, system-ui, sans-serif",
      }}
    >
      <div
        // Outer shell is light (#F6F8FB — the same neutral surface used
        // on StandingsWidget / SecuritySettings cards) so the call modal
        // matches the rest of the app's light theme. No more radial
        // "AI-generated" gradients. The video area inside still uses a
        // dark backdrop because video reads better on dark (every other
        // video app does the same — Meet, Zoom, Teams).
        className="tp-call-modal"
        style={{
          width: "100%",
          height: "100%",
          maxWidth: 960,
          maxHeight: "92vh",
          background: "#F6F8FB",
          borderRadius: 16,
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 30px 80px rgba(0,0,0,0.25)",
          color: "#1A212F",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Chrome
          title={isVideo ? "Video call" : "Audio call"}
          subtitle={headerSubtitle}
          accent={headerAccent}
          onMinimize={isJoined ? toggleMinimize : undefined}
          onClose={onEndCall}
        />

        {/* Body */}
        <div
          style={{
            flex: 1,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* Active header strip (joined audio + connecting states) */}
          {(!isVideo || !isJoined) && (
            <ActiveHeader
              contact={contact}
              recording={isRecording}
              joinedAt={joinedAt}
              participantCount={participantCount}
            />
          )}

          {/* Stage */}
          {isConnecting && (
            <CenterStage>
              <Avatar name={contact.name} size={180} pulse />
              <Identity contact={contact} />
              <Status text="Connecting" dots />
              <SecureChip />
            </CenterStage>
          )}

          {isJoined && !isVideo && (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 40px",
                gap: 28,
              }}
            >
              <Avatar name={contact.name} size={120} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 600 }}>
                  {contact.name}
                </div>
                <div
                  style={{ fontSize: 13, color: TP.text2, marginTop: 4 }}
                >
                  {contact.role}
                </div>
              </div>
              <div style={{ width: "100%", maxWidth: 440 }}>
                <Waveform speaking={!isMute} />
              </div>
              <SecureChip />
            </div>
          )}

          {isJoined && isVideo && (
            <div
              // Video stage is a rounded dark "screen" inset 16px from
              // the modal's light surround — feels like a TV/monitor
              // sitting on a light shelf. Inset bottom reserves room
              // for the floating control bar.
              //
              // We HIDE the SDK's built-in participants bar
              // (participantsBarPosition={false}) and render our own
              // local self-view as a small floating PiP at top-right
              // INSIDE this dark plate. This is what every consumer
              // video app does (Meet, Zoom, Teams) and avoids the
              // wasted-black-space + buttons-overlap problems the bar
              // created in 1:1 calls.
              style={{
                position: "absolute",
                top: 16,
                left: 16,
                right: 16,
                bottom: 100,
                borderRadius: 14,
                overflow: "hidden",
                background: "#0a0e18",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
              }}
              className="tp-call-video-stage"
            >
              <StreamTheme>
                <div style={{ width: "100%", height: "100%", position: "relative" }}>
                  <SpeakerLayout
                    participantsBarPosition={false}
                    ParticipantViewUISpotlight={TpParticipantUI}
                    ParticipantViewUIBar={TpParticipantUI}
                  />

                  {/* Floating local self-view PiP — top-right of the
                      video area, just below the chrome strip. Hidden
                      while screen-sharing (the screen-share IS the
                      local stream and is already in the spotlight). */}
                  {localParticipant && screenShareStatus !== "enabled" && (
                    <div
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        width: 180,
                        height: 120,
                        borderRadius: 12,
                        overflow: "hidden",
                        border: `2px solid ${TP.blue}`,
                        boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
                        background: "#0a0e18",
                        zIndex: 6,
                      }}
                    >
                      <ParticipantView
                        participant={localParticipant}
                        muteAudio
                        trackType="videoTrack"
                        ParticipantViewUI={TpParticipantUI}
                      />
                    </div>
                  )}
                </div>
              </StreamTheme>
            </div>
          )}

          {/* Bottom control bar */}
          {isJoined && (
            <ControlBar
              isVideo={isVideo}
              muted={isMute}
              onMute={handleToggleMic}
              cameraOff={!isCameraEnabled}
              onCamera={handleToggleCamera}
              sharing={screenShareStatus === "enabled"}
              onShare={handleToggleScreenShare}
              recording={isRecording}
              onRecord={handleToggleRecording}
              canRecord={canRecord}
              onEnd={onEndCall}
              openMenu={openMenu}
              toggleDropdown={toggleDropdown}
              closeDropdown={closeDropdown}
              devices={devices}
              micLevel={micLevel}
              onSelectMic={handleSelectMic}
              onSelectSpeaker={handleSelectSpeaker}
              onSelectCamera={handleSelectCamera}
              blurSupported={blurSupported}
              blurActive={blurActive}
              blurReady={blurReady}
              onToggleBlur={handleToggleBlur}
              recordingPending={isRecordingPending}
              onOpenSettings={openSettings}
              onOpenAddParticipant={openAddParticipant}
            />
          )}

          {/* Slide-in Settings Panel */}
          {showSettings && (
            <SettingsPanel
              onClose={() => setShowSettings(false)}
              devices={devices}
              onSelectMic={handleSelectMic}
              onSelectSpeaker={handleSelectSpeaker}
              onSelectCamera={handleSelectCamera}
              isVideo={isVideo}
            />
          )}

          {/* Add-Participant Overlay */}
          {showAddParticipant && (
            <AddParticipantOverlay
              call={call}
              chatClient={chatClient}
              onClose={() => setShowAddParticipant(false)}
            />
          )}

          {/* Cancel button while still connecting */}
          {isConnecting && (
            <div
              style={{
                padding: "24px 40px 32px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <BigAction
                icon={I.phoneOff}
                label="Cancel"
                color={TP.danger}
                rotate
                onClick={onEndCall}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-pieces ──────────────────────────────────────────────────────────

const CenterStage = ({ children }) => (
  <div
    style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 16,
      padding: "20px 40px",
    }}
  >
    {children}
  </div>
);

const Identity = ({ contact }) => (
  <div style={{ textAlign: "center", marginTop: 8 }}>
    <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: -0.3 }}>
      {contact.name}
    </div>
    <div style={{ fontSize: 14, color: TP.text2, marginTop: 4 }}>
      {contact.role}
    </div>
  </div>
);

const Status = ({ text, dots }) => (
  <div
    style={{
      fontSize: 13,
      color: TP.text2,
      marginTop: 4,
      letterSpacing: 0.3,
      display: "flex",
      alignItems: "center",
      gap: 2,
    }}
  >
    {text}
    {dots && (
      <span className="tp-dots">
        <span />
        <span />
        <span />
      </span>
    )}
  </div>
);

function ActiveHeader({ contact, recording, joinedAt, participantCount }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 16px",
        borderBottom: `1px solid ${TP.border}`,
        background: "#FFFFFF",
      }}
    >
      <Avatar name={contact.name} size={32} online />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {contact.name}
        </div>
        <div style={{ fontSize: 11, color: TP.text2 }}>
          {participantCount} participant{participantCount === 1 ? "" : "s"}
        </div>
      </div>
      {recording && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "rgba(239,68,68,0.16)",
            border: `1px solid ${TP.danger}`,
            padding: "4px 9px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            color: TP.danger,
          }}
        >
          <span
            className="tp-dot-pulse"
            style={{
              background: TP.danger,
              width: 6,
              height: 6,
              borderRadius: 999,
            }}
          />
          REC
        </div>
      )}
      {joinedAt && <Timer startedAt={joinedAt} />}
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <Bars level={4} />
        <span style={{ fontSize: 10, color: TP.text2, fontWeight: 600 }}>
          HD
        </span>
      </div>
    </div>
  );
}

function ControlBar({
  isVideo,
  muted,
  onMute,
  cameraOff,
  onCamera,
  sharing,
  onShare,
  recording,
  onRecord,
  canRecord,
  onEnd,
  // Phase 2 — device-source props
  openMenu,
  toggleDropdown,
  closeDropdown,
  devices,
  micLevel,
  onSelectMic,
  onSelectSpeaker,
  onSelectCamera,
  blurSupported,
  blurActive,
  blurReady,
  onToggleBlur,
  recordingPending,
  onOpenSettings,
  onOpenAddParticipant,
}) {
  // Control bar sits OUTSIDE the dark video plate on the modal's
  // light surround. Background is transparent so the modal's light
  // grey shows through. No more "gradient to black" overlay.
  return (
    <div
      style={{
        padding: "14px 16px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        position: isVideo ? "absolute" : "relative",
        bottom: isVideo ? 0 : "auto",
        left: 0,
        right: 0,
        zIndex: 5,
        background: "transparent",
      }}
    >
      {/* Mic — main click toggles mute, chevron opens device picker */}
      <CtrlButton
        icon={muted ? I.micOff : I.mic}
        label={muted ? "Unmute" : "Mute"}
        mute={muted}
        onClick={onMute}
        withChevron
        onChevron={() => toggleDropdown("mic")}
        chevOpen={openMenu === "mic"}
        dropdown={
          openMenu === "mic" && (
            <SourceMenu
              title="Select microphone"
              items={devices.mic.devices}
              activeId={devices.mic.selectedDeviceId}
              onPick={onSelectMic}
              onClose={closeDropdown}
              level={micLevel}
              showLevels
              emptyHint={
                devices.mic.hasBrowserPermission
                  ? "No microphones detected"
                  : "Microphone permission denied"
              }
            />
          )
        }
      />

      {/* Camera (video only) — main click toggles, chevron opens picker */}
      {isVideo && (
        <CtrlButton
          icon={cameraOff ? I.camOff : I.cam}
          label={cameraOff ? "Turn camera on" : "Turn camera off"}
          mute={cameraOff}
          onClick={onCamera}
          withChevron
          onChevron={() => toggleDropdown("cam")}
          chevOpen={openMenu === "cam"}
          dropdown={
            openMenu === "cam" && (
              <SourceMenu
                title="Select camera"
                items={devices.camera.devices}
                activeId={devices.camera.selectedDeviceId}
                onPick={onSelectCamera}
                onClose={closeDropdown}
                emptyHint={
                  devices.camera.hasBrowserPermission
                    ? "No cameras detected"
                    : "Camera permission denied"
                }
              />
            )
          }
        />
      )}

      {/* Speaker device picker. Click opens the dropdown directly — no
          mute-output click action because the SDK has no real mute-output
          control. Hidden entirely on browsers without output device
          selection (Firefox, mobile Safari). */}
      {devices.speaker.isSupported && (
        <CtrlButton
          icon={I.speaker}
          label="Select speaker"
          onClick={() => toggleDropdown("speaker")}
          withChevron
          onChevron={() => toggleDropdown("speaker")}
          chevOpen={openMenu === "speaker"}
          dropdown={
            openMenu === "speaker" && (
              <SourceMenu
                title="Select speaker"
                items={devices.speaker.devices}
                activeId={devices.speaker.selectedDeviceId}
                onPick={onSelectSpeaker}
                onClose={closeDropdown}
                emptyHint="No output devices detected"
              />
            )
          }
        />
      )}

      {/* Background blur (video only, supported browsers only). While the
          TFLite model loads we dim the button to 40% opacity so the user
          can see it's not ready yet — but the click handler stays wired
          up so taps during load fire a "loading" toast instead of silently
          doing nothing. */}
      {blurSupported && (
        <div style={{ opacity: blurReady ? 1 : 0.4 }}>
          <CtrlButton
            icon={I.blur}
            label={
              !blurReady
                ? "Background blur loading…"
                : blurActive
                ? "Disable background blur"
                : "Enable background blur"
            }
            active={blurActive}
            onClick={onToggleBlur}
          />
        </div>
      )}

      {/* Screen share (video only, no source picker) */}
      {isVideo && (
        <CtrlButton
          icon={I.share}
          label={sharing ? "Stop sharing" : "Share screen"}
          active={sharing}
          onClick={onShare}
        />
      )}

      {/* Recording — only rendered when this call type actually allows
          recording (Stream capability "start-record-call"). Visual
          loading state while the SDK call is in flight to avoid
          double-clicks. */}
      {canRecord && (
        <div style={{ opacity: recordingPending ? 0.5 : 1 }}>
          <CtrlButton
            icon={I.rec}
            label={
              recordingPending
                ? "Working…"
                : recording
                ? "Stop recording"
                : "Start recording"
            }
            danger={recording}
            onClick={recordingPending ? undefined : onRecord}
          />
        </div>
      )}

      {/* Add participant */}
      <CtrlButton
        icon={I.addUser}
        label="Add participant"
        onClick={onOpenAddParticipant}
      />

      {/* Settings */}
      <CtrlButton
        icon={I.gear}
        label="Settings"
        onClick={onOpenSettings}
      />

      <div
        style={{
          width: 1,
          height: 28,
          background: TP.border,
          margin: "0 2px",
        }}
      />

      <button
        onClick={onEnd}
        aria-label="End call"
        className="tp-big-btn"
        style={{
          background: TP.danger,
          color: "#fff",
          border: "none",
          borderRadius: 12,
          padding: "12px 22px",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          fontWeight: 600,
          fontSize: 13,
          boxShadow: "0 6px 20px rgba(239,68,68,0.4)",
        }}
      >
        <Icon d={I.phone} size={18} stroke={2.2} fill="currentColor" />
        End
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MiniWindow — 320x200 floating widget at bottom-left while call runs.
// Kept intentionally narrow: only mute + camera (if video) + end + expand.
// Source stays the same SDK call; this is purely a layout change.
// ─────────────────────────────────────────────────────────────────────
function MiniWindow({
  contact,
  callType,
  muted,
  onMute,
  cameraOff,
  onCamera,
  onExpand,
  onEnd,
  joinedAt,
  isJoined,
}) {
  const isVideo = callType === "video";
  return (
    <div
      style={{
        position: "fixed",
        // Bottom-LEFT (not bottom-right) so the mini-window doesn't sit
        // on top of the floating support icon, which lives at bottom-right.
        left: 20,
        bottom: 20,
        zIndex: 60,
        width: 320,
        height: 200,
        borderRadius: 14,
        overflow: "hidden",
        background: isVideo ? "#0a0e18" : TP.modal,
        boxShadow:
          "0 20px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.08)",
        color: TP.text,
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      {isVideo && isJoined ? (
        <div style={{ position: "absolute", inset: 0 }}>
          <StreamTheme>
            <SpeakerLayout participantsBarPosition={false} />
          </StreamTheme>
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: 14,
          }}
        >
          <Avatar name={contact.name} size={48} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {contact.name}
            </div>
            <div
              style={{
                fontSize: 11,
                color: TP.text2,
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginTop: 2,
              }}
            >
              <span
                className="tp-dot-pulse"
                style={{
                  background: TP.ok,
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                }}
              />
              {joinedAt && <Timer startedAt={joinedAt} />}
            </div>
          </div>
        </div>
      )}

      {/* Status pill, top-left */}
      <div
        style={{
          position: "absolute",
          top: 8,
          left: 8,
          padding: "3px 6px",
          background: "rgba(0,0,0,0.5)",
          borderRadius: 6,
          fontSize: 10,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 5,
          color: "#FFFFFF",
        }}
      >
        <span
          style={{ width: 5, height: 5, borderRadius: "50%", background: TP.ok }}
        />
        {isVideo ? "Live" : "On call"}
      </div>

      {/* Expand button, top-right */}
      <button
        onClick={onExpand}
        aria-label="Expand call"
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "rgba(0,0,0,0.5)",
          border: "none",
          color: "#FFFFFF",
          padding: 5,
          borderRadius: 6,
          cursor: "pointer",
          display: "flex",
        }}
      >
        <Icon d={I.minimize} size={12} />
      </button>

      {/* Mini controls */}
      <div
        style={{
          position: "absolute",
          bottom: 10,
          left: 10,
          right: 10,
          display: "flex",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <MiniBtn
          icon={muted ? I.micOff : I.mic}
          mute={muted}
          onClick={onMute}
          label="Toggle mic"
        />
        {isVideo && (
          <MiniBtn
            icon={cameraOff ? I.camOff : I.cam}
            mute={cameraOff}
            onClick={onCamera}
            label="Toggle camera"
          />
        )}
        <MiniBtn icon={I.phone} danger onClick={onEnd} label="End call" />
      </div>
    </div>
  );
}

function MiniBtn({ icon, mute, danger, onClick, label }) {
  const bg = danger
    ? TP.danger
    : mute
    ? "rgba(239,68,68,0.2)"
    : "rgba(255,255,255,0.12)";
  const fg = danger ? "#fff" : mute ? "#fca5a5" : "#fff";
  return (
    <button
      onClick={onClick}
      aria-label={label}
      style={{
        width: 32,
        height: 32,
        borderRadius: 9,
        border: "none",
        cursor: "pointer",
        background: bg,
        color: fg,
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon
        d={icon}
        size={14}
        stroke={2.2}
        fill={danger ? "currentColor" : "none"}
      />
    </button>
  );
}

function BigAction({ icon, label, color, rotate, onClick }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
      }}
    >
      <button
        onClick={onClick}
        aria-label={label}
        className="tp-big-btn"
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          border: "none",
          background: color,
          color: "#fff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 8px 24px ${color}55, 0 0 0 6px ${color}1f`,
          transform: rotate ? "rotate(135deg)" : "none",
        }}
      >
        <Icon d={icon} size={26} stroke={2.2} fill="currentColor" />
      </button>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: TP.text2,
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
    </div>
  );
}
